import { Injectable } from '@angular/core';
import * as convertKeys from 'convert-keys';
import { Space } from '../model/space.model';
import { ApiService } from './api.service';

@Injectable()
export class FinancialService {
  constructor(private apiService: ApiService) {}

  async getMarkets(): Promise<Space[]> {
    try {
      const resp = await this.apiService.get(`types/market`);
      return convertKeys.toCamel<Space[]>(resp.spaces || []);
    } catch (err) {
      console.log('Could not load financial markets.', err);
      return [];
    }
  }

  async getZones(marketId: string): Promise<Space[]> {
    try {
      const resp = await this.apiService.get(`types/zone`, { market_id: marketId });
      return convertKeys.toCamel<Space[]>(resp.spaces || []);
    } catch (err) {
      console.log(`Could not load zones for market ${marketId}.`, err);
      return [];
    }
  }

  async getRegistrationFinancialData(
    registrationId: string,
  ): Promise<{ points: any[]; zones: Space[]; market: Space | null }> {
    try {
      const resp = await this.apiService.get(`registrations/${registrationId}/financial-points`);
      return {
        points: resp && resp.points ? convertKeys.toCamel<any[]>(resp.points) : [],
        zones: resp && resp.zones ? convertKeys.toCamel<Space[]>(resp.zones) : [],
        market: resp && resp.market ? convertKeys.toCamel<Space>(resp.market) : null,
      };
    } catch (err) {
      console.log(`Could not load the financial data for registration ${registrationId}.`, err);
      return { points: [], zones: [], market: null };
    }
  }

  /**
   * Whether the current user may add/remove registration - zone links.
   * Check if Energy Markets roles
   */
  async canManageFinancialZones(): Promise<boolean> {
    try {
      const details = await this.apiService.get('user/details');
      const resources = (details && details.resources) || [];
      return resources.some(
        (resource: any) => resource && (resource.resource_name === 'MARKET' || resource.resource_name === 'MARKET_ZONE'),
      );
    } catch (err) {
      console.log('Could not determine financial-zone management permission.', err);
      return false;
    }
  }

  async getFinancialPoints(zoneId: string): Promise<any[]> {
    try {
      const resp = await this.apiService.get(`zones/${zoneId}/financial-points`);
      return convertKeys.toCamel<any[]>(resp || []);
    } catch (err) {
      console.log(`Could not load financial points for zone ${zoneId}.`, err);
      return [];
    }
  }

  async setZoneRelationship(registrationId: string, zoneId: string, method: 'add' | 'remove') {
    return this.apiService.put(`registrations/${registrationId}/zones/${zoneId}`, { method });
  }
}
